import { Fragment, useCallback, type ReactNode, useMemo, useRef } from "react";
import type { ChatMessage } from "@ferrivia/api-contract";
import { useFormatters, useTranslation } from "@ferrivia/i18n";
import { Reply } from "lucide-react";
import type { OutboxEntry } from "../useChatThread";
import { MessageBlocks } from "../MessageBlocks";
import { DeliveryState } from "../DeliveryState";
import { ReplyQuote } from "../ReplyQuote";
import { useStickToBottom } from "../useStickToBottom";
import { useSwipeToReply } from "../useSwipeToReply";
import s from "./MessageList.module.css";

export interface MessageListProps {
  messages: ChatMessage[];
  outbox: OutboxEntry[];
  isOwnMessage: (message: ChatMessage) => boolean;
  senderName?: (message: ChatMessage) => string | undefined;
  onRetry: (clientMessageId: string) => void;
  onReply?: (message: ChatMessage) => void;
  topNote?: ReactNode;
  className?: string;
}

function MessageRow({
  message,
  own,
  label,
  formatTime,
  onReply,
}: {
  message: ChatMessage;
  own: boolean;
  label: string | undefined;
  formatTime: (iso: string) => string;
  onReply?: (message: ChatMessage) => void;
}) {
  const { t } = useTranslation("chat");
  const handleReply = useCallback(() => onReply?.(message), [onReply, message]);
  const swipe = useSwipeToReply(handleReply);

  function scrollToMessage(id: string) {
    const el = document.querySelector(`[data-message-id="${id}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add(s.highlight);
      setTimeout(() => el.classList.remove(s.highlight), 1500);
    }
  }

  return (
    <div className={`${s.row} ${own ? s.rowMe : s.rowThem}`}>
      <div className={s.bubbleWrap} data-message-id={message.id}>
        <div
          className={`${s.msg} ${own ? s.me : s.them}`}
          ref={swipe.ref}
          onPointerDown={swipe.onPointerDown}
          onPointerMove={swipe.onPointerMove}
          onPointerUp={swipe.onPointerUp}
          onPointerCancel={swipe.onPointerCancel}
        >
          {message.replyTo ? (
            <ReplyQuote
              reply={message.replyTo}
              isOwnReply={own}
              onClick={() => scrollToMessage(message.replyTo!.id)}
            />
          ) : null}
          {label ? <div className={s.sender}>{label}</div> : null}
          <MessageBlocks body={message.body} />
          <div className={s.time}>{formatTime(message.createdAt)}</div>
        </div>
        <div ref={swipe.iconRef} className={s.swipeIndicator} aria-hidden="true">
          <Reply size={18} />
        </div>
      </div>
      {onReply ? (
        <button
          type="button"
          className={s.hoverReply}
          onClick={handleReply}
          aria-label={t("reply.replyAction", "Vastaa")}
        >
          <Reply size={16} aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

export function MessageList({ messages, outbox, isOwnMessage, senderName, onRetry, onReply, topNote, className }: MessageListProps) {
  const { tag } = useFormatters();
  const { formatTime, formatDay } = useMemo(() => {
    const timeFormat = new Intl.DateTimeFormat(tag, { hour: "2-digit", minute: "2-digit", hour12: false });
    const dayFormat = new Intl.DateTimeFormat(tag, { day: "numeric", month: "long" });
    return {
      formatTime: (iso: string) => timeFormat.format(new Date(iso)),
      formatDay: (iso: string) => dayFormat.format(new Date(iso)),
    };
  }, [tag]);

  let lastDay = "";
  const classes = className ? `${s.root} ${className}` : s.root;
  const containerRef = useRef<HTMLDivElement>(null);
  useStickToBottom(containerRef, messages, outbox);

  return (
    <div ref={containerRef} className={classes}>
      {topNote ? <div className={s.careNote}>{topNote}</div> : null}
      {messages.map((message) => {
        const day = formatDay(message.createdAt);
        const showSep = day !== lastDay;
        lastDay = day;
        const own = isOwnMessage(message);
        const label = own ? undefined : senderName?.(message);
        return (
          <Fragment key={message.id}>
            {showSep ? <div className={s.daySep}>{day}</div> : null}
            <MessageRow
              message={message}
              own={own}
              label={label}
              formatTime={formatTime}
              onReply={onReply}
            />
          </Fragment>
        );
      })}
      {outbox.map((entry) => (
        <div key={entry.clientMessageId} className={`${s.row} ${s.rowMe}`}>
          <div className={s.bubbleWrap}>
            <div className={`${s.msg} ${s.me}`}>
              {entry.replyTo ? <ReplyQuote reply={entry.replyTo} isOwnReply /> : null}
              <MessageBlocks body={entry.body} />
              <div className={s.time}>
                <DeliveryState state={entry.state} onRetry={() => onRetry(entry.clientMessageId)} />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
